import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useLiquidityCacheQuote } from '../hooks/useLiquidityCacheQuote';
import { NiceModeAlert } from './NiceModeAlert';
import { LcPoolSummary } from '../hooks/useLiquidityCachePools';
import { api } from '@/lib/api';

interface TradePanelProps {
  pool: LcPoolSummary;
  onTradeComplete: () => void;
}

export function TradePanel({ pool, onTradeComplete }: TradePanelProps) {
  const [direction, setDirection] = useState<'a_to_b' | 'b_to_a'>('a_to_b');
  const [inputAmount, setInputAmount] = useState('');
  const [minOutputAmount, setMinOutputAmount] = useState('');
  const [niceMode, setNiceMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputSymbol = direction === 'a_to_b' ? pool.tokenA.symbol : pool.tokenB.symbol;
  const outputSymbol = direction === 'a_to_b' ? pool.tokenB.symbol : pool.tokenA.symbol;
  const inputSource = direction === 'a_to_b' ? pool.tokenA.source : pool.tokenB.source;
  const outputSource = direction === 'a_to_b' ? pool.tokenB.source : pool.tokenA.source;

  const { quote, loading: quoteLoading } = useLiquidityCacheQuote(
    pool.poolId,
    direction,
    inputAmount || undefined,
    niceMode,
  );

  const handleTrade = async () => {
    if (!inputAmount) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.liquidityCacheTrade(pool.poolId, {
        direction,
        inputAmount,
        minOutputAmount: minOutputAmount || undefined,
        niceMode,
      });
      setInputAmount('');
      setMinOutputAmount('');
      onTradeComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Trade failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={direction === 'a_to_b' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setDirection('a_to_b')}
        >
          {pool.tokenA.symbol} → {pool.tokenB.symbol}
        </Button>
        <Button
          variant={direction === 'b_to_a' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setDirection('b_to_a')}
        >
          {pool.tokenB.symbol} → {pool.tokenA.symbol}
        </Button>
      </div>

      <div>
        <Label>Input Amount ({inputSymbol})</Label>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={inputAmount}
            onChange={(e) => setInputAmount(e.target.value)}
            placeholder="0"
          />
          {inputSource === 'external' && (
            <Badge variant="outline" className="text-xs whitespace-nowrap">
              Simulated
            </Badge>
          )}
        </div>
      </div>

      <div>
        <Label>Min Output ({outputSymbol}, optional)</Label>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={minOutputAmount}
            onChange={(e) => setMinOutputAmount(e.target.value)}
            placeholder="Slippage protection"
          />
          {outputSource === 'external' && (
            <Badge variant="outline" className="text-xs whitespace-nowrap">
              Simulated
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="niceMode"
          checked={niceMode}
          onCheckedChange={(c) => setNiceMode(c === true)}
        />
        <Label htmlFor="niceMode" className="text-sm cursor-pointer">
          Nice Mode (minimize rounding loss)
        </Label>
      </div>

      {quote && (
        <div className="rounded-md border p-3 text-sm space-y-1">
          <p>
            Estimated output:{' '}
            <span className="font-medium">
              {quote.amountOut} {outputSymbol}
            </span>
          </p>
          <p>
            Fee:{' '}
            <span className="font-medium">
              {quote.feeAmount} {inputSymbol}
            </span>
          </p>
          <p>
            Price impact:{' '}
            <span className="font-medium">{(quote.priceImpactBps / 100).toFixed(2)}%</span>
          </p>
          {niceMode && quote.niceMode && (
            <NiceModeAlert quote={quote} inputSymbol={inputSymbol} outputSymbol={outputSymbol} />
          )}
        </div>
      )}
      {quoteLoading && <p className="text-sm text-muted-foreground">Loading quote...</p>}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <Button onClick={handleTrade} disabled={!inputAmount || submitting} className="w-full">
        {submitting ? 'Trading...' : 'Trade'}
      </Button>
    </div>
  );
}
